import { useCallback, useEffect, useRef, useState } from 'react';

const INITIAL_STATE = { data: undefined, loading: false, error: undefined, called: false };

export default function useAsyncCall(asyncFn, { onCompleted, onError } = {}) {
  const [state, setState] = useState(INITIAL_STATE);
  const asyncFnRef = useRef(asyncFn);
  asyncFnRef.current = asyncFn;
  const onCompletedRef = useRef(onCompleted);
  onCompletedRef.current = onCompleted;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const latestCallIdRef = useRef(0);

  // Apollo tears down its subscription on unmount and delivers nothing. Without this, a late
  // response fires onCompleted/onError from a dead component — and these callbacks navigate
  // (FlightExpanded), so a user who already left the page would be yanked to another route.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const execute = useCallback(async (...args) => {
    // Latest-execute-wins, like Apollo superseding an in-flight query with a refetch: the
    // search retry timers (Results/FormReservation) fire a second identical request at 27s,
    // and without this the ORIGINAL's late failure would overwrite the retry's result and
    // navigate to the error page even though the retry was about to succeed.
    latestCallIdRef.current += 1;
    const callId = latestCallIdRef.current;
    const isLive = () => callId === latestCallIdRef.current && mountedRef.current;
    setState((prev) => ({ ...prev, loading: true, called: true }));
    let data;
    try {
      data = await asyncFnRef.current(...args);
    } catch (error) {
      if (isLive()) {
        setState({ data: undefined, loading: false, error, called: true });
        onErrorRef.current?.(error);
      }
      return { error };
    }
    if (isLive()) {
      setState({ data, loading: false, error: undefined, called: true });
      // Outside the fetch try/catch: Apollo never routes an exception thrown by the consumer's
      // onCompleted into onError — doing so turned a successful response into an error state
      // (and, in FlightExpanded, into an error-page navigation).
      try {
        onCompletedRef.current?.(data);
      } catch (callbackError) {
        // eslint-disable-next-line no-console
        console.error('useAsyncCall: onCompleted callback threw', callbackError);
      }
    }
    return { data };
  }, []);

  return [execute, { ...state, refetch: execute }];
}
